/**
 * API call logic for the fix-errors page.
 * Handles bulk list updates for both property and order flows.
 */

import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { useNavigationService } from '@/services/navigate-service';
import { ROUTES } from '@/config/routes';

import {
  getFileFailureDataAPI,
  updateBulkListAPI,
  updateBulkListForOrderAPI,
} from '../../service/apiService';

import type {
  PropertyErrorFormData,
  OrderErrorFormData,
} from '@/types/property.types';
import type { ErrorCell } from '../utils/fixErrorFieldMaps';
import { parsePropertyErrors, parseOrderErrors } from './useErrorDataTransform';
import type { FixErrorModal } from '../types/modal-state';
import { NO_MODAL } from '../types/modal-state';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

interface ApiCallbacks {
  setLoading: (v: boolean) => void;
  setPopUpLoader: (v: boolean) => void;
  setPopUpLoaderForOrder: (v: boolean) => void;
  setModal: (state: FixErrorModal) => void;
  setErrorTableData: (data: ErrorTableRow[]) => void;
  setResult: (data: ErrorTableRow[]) => void;
  setFailedCountAfterFix: (count: number) => void;
  setSuccessCountAfterFix: (count: number) => void;
  setFixIssueLoading: (v: boolean) => void;
  setIsClearData: (v: boolean) => void;
  setFileChangeAfterFix: (v: boolean) => void;
  setFileIdForOrder: (id: string) => void;
  setSuccessDataAfterFix: (count: number) => void;
  // Parent callbacks
  setOrderCountInfo: (count: number) => void;
  setFailedData: (data: string) => void;
  setFileId: (id: string) => void;
  setAmount: (amount: number) => void;
  setSuccessData: (data: string) => void;
  setFixErrorTableData: (data: string) => void;
  setSkippedStepFromStepper: (step: number) => void;
  setActiveStep: (step: number) => void;
}

interface UseFixErrorApiParams {
  isProperty: boolean;
  fileState: boolean;
  successCountAfterFix: number;
  callbacks: ApiCallbacks;
}

// ---- Hook -----------------------------------------------------------------

export const useFixErrorApi = ({
  isProperty,
  fileState,
  successCountAfterFix,
  callbacks,
}: UseFixErrorApiParams) => {
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();

  const callBulkListUpdate = async (
    formData: PropertyErrorFormData[]
  ): Promise<void> => {
    callbacks.setLoading(true);
    try {
      const response = await updateBulkListAPI(formData);
      if (response) {
        const responseData = response.data as Record<string, unknown>[];
        if (responseData.length > 0) {
          const { rows } = parsePropertyErrors(responseData);
          callbacks.setErrorTableData(rows);
          callbacks.setResult(rows);
          callbacks.setFailedCountAfterFix(responseData.length);
          const newCount =
            successCountAfterFix + (formData.length - responseData.length);
          callbacks.setSuccessCountAfterFix(newCount);
        } else {
          navigateTo(
            isProperty
              ? ROUTES.PROPERTIES_NEW_IMPORT
              : ROUTES.ORDERS_NEW_IMPORT
          );
          dispatch(
            showAlert({
              type: Variants.SUCCESS,
              message: 'Changes saved successfully.',
            })
          );
        }
      }
      callbacks.setModal(NO_MODAL);
    } catch (error: unknown) {
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    } finally {
      callbacks.setLoading(false);
      callbacks.setPopUpLoader(false);
    }
  };

  const callBulkListUpdateForOrder = async (
    formData: OrderErrorFormData[]
  ): Promise<void> => {
    callbacks.setLoading(true);
    try {
      const response = await updateBulkListForOrderAPI(formData);
      if (response) {
        const responseData = response.data as {
          orderApproved: number;
          orderRejected: number;
          fileId: string;
          subTotal: number;
        };

        callbacks.setOrderCountInfo(responseData.orderApproved);
        callbacks.setFileId(responseData.fileId);
        callbacks.setFileIdForOrder(responseData.fileId);
        callbacks.setAmount(responseData.subTotal);

        if (!fileState) {
          callbacks.setSuccessData(String(responseData.orderApproved));
        }
        callbacks.setSuccessDataAfterFix(responseData.orderApproved);
        callbacks.setFailedData(String(responseData.orderRejected));

        if (responseData.orderRejected === 0) {
          callbacks.setSkippedStepFromStepper(2);
          callbacks.setActiveStep(3);
          callbacks.setModal(NO_MODAL);
        } else {
          // Replace the originating optional-warnings modal with the
          // partial-success modal in a single transition.
          callbacks.setModal({ kind: 'partial-success' });
        }
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      if (
        axiosError?.response?.data?.message?.includes('Zip') ||
        axiosError?.response?.data?.message?.includes('zip code') ||
        axiosError?.response?.data?.message?.includes('Zip code')
      ) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: 'Invalid zip code please check',
          })
        );
      } else {
        dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
      }
      callbacks.setModal(NO_MODAL);
    } finally {
      callbacks.setLoading(false);
      callbacks.setPopUpLoaderForOrder(false);
    }
  };

  const fixIssues = async (fileIdForOrder: string): Promise<void> => {
    callbacks.setFixIssueLoading(true);
    try {
      const fileData = await getFileFailureDataAPI(fileIdForOrder, false);
      if (fileData) {
        const { rows, failedCount } = parseOrderErrors(fileData.data);

        if (!fileState) {
          callbacks.setSuccessData(String(rows.length - failedCount));
        }
        callbacks.setFailedData(String(failedCount));
        callbacks.setFixErrorTableData(JSON.stringify(rows));
        callbacks.setFixIssueLoading(false);
        callbacks.setErrorTableData(rows);
        callbacks.setIsClearData(true);
        callbacks.setFileChangeAfterFix(true);
        callbacks.setModal(NO_MODAL);
      }
    } catch (error: unknown) {
      callbacks.setFixIssueLoading(false);
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    }
  };

  return { callBulkListUpdate, callBulkListUpdateForOrder, fixIssues };
};
